import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  Stack,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import crmPlusService, { CrmAttachment } from '../../services/crmPlusService';
import { TypePill, ErrorState, TableEmptyState } from '../../components/common/pageKit';

/**
 * Metadata-only document register for a CRM entity — this stores a file
 * name plus an optional link to where the document actually lives (a DMS,
 * a shared drive, an email...). There is no upload endpoint behind it, so
 * the UI must not imply one.
 */

export type CrmAttachmentEntityType = 'lead' | 'opportunity' | 'customer';

export interface CrmAttachmentsPanelProps {
  entityType: CrmAttachmentEntityType;
  entityId: number;
}

const fmtDate = (v?: string | null): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const emptyForm = { fileName: '', url: '', docCategory: '', notes: '' };

const CrmAttachmentsPanel: React.FC<CrmAttachmentsPanelProps> = ({ entityType, entityId }) => {
  const [attachments, setAttachments] = useState<CrmAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    if (!entityType || !entityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    crmPlusService
      .getAttachments(entityType, entityId)
      .then((res) => setAttachments(res?.attachments || []))
      .catch((e: any) => setError(e?.message || 'Failed to load documents.'))
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm(emptyForm);
    setSaveError(null);
    setAddOpen(true);
  };

  const submitAdd = () => {
    if (!form.fileName.trim()) {
      setSaveError('A file name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    crmPlusService
      .createAttachment(entityType, entityId, {
        fileName: form.fileName.trim(),
        url: form.url.trim() || undefined,
        docCategory: form.docCategory.trim() || undefined,
        notes: form.notes.trim() || undefined,
      })
      .then(() => {
        setAddOpen(false);
        load();
      })
      .catch((e: any) => setSaveError(e?.message || 'Failed to add this document link.'))
      .finally(() => setSaving(false));
  };

  const confirmDelete = () => {
    if (deleteId === null) return;
    setDeleting(true);
    crmPlusService
      .deleteAttachment(deleteId)
      .then(() => {
        setDeleteId(null);
        load();
      })
      .catch((e: any) => setError(e?.message || 'Failed to delete this document link.'))
      .finally(() => setDeleting(false));
  };

  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, p: 2, pb: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Documents
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Links to documents stored elsewhere — no files are uploaded here.
          </Typography>
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={openAdd} sx={{ flexShrink: 0 }}>
          Add document link
        </Button>
      </Box>

      <Divider />

      <Box sx={{ p: loading || error || attachments.length === 0 ? 0 : 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : attachments.length === 0 ? (
          <TableEmptyState title="No document links yet" hint="Add a link to a document stored in your DMS, drive, or inbox." />
        ) : (
          <Stack divider={<Divider />}>
            {attachments.map((a) => (
              <Box
                key={a.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25 }}
              >
                <DescriptionOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {a.url ? (
                      <MuiLink
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 260,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                        title={a.file_name}
                      >
                        <InsertLinkIcon sx={{ fontSize: 14 }} />
                        {a.file_name}
                      </MuiLink>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}
                        title={a.file_name}
                      >
                        {a.file_name}
                      </Typography>
                    )}
                    {a.doc_category && <TypePill label={a.doc_category} tint="#0284c7" />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {a.uploaded_by_name || 'Unknown'} · {fmtDate(a.created_at)}
                    {a.notes ? ` · ${a.notes}` : ''}
                  </Typography>
                </Box>
                <Tooltip title="Remove link">
                  <IconButton size="small" onClick={() => setDeleteId(a.id)} sx={{ flexShrink: 0 }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Add document link */}
      <Dialog open={addOpen} onClose={() => !saving && setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add document link</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              This records a file name and, optionally, a link to where it lives — nothing is uploaded to this ERP.
            </Typography>
            <TextField
              label="File name"
              required
              value={form.fileName}
              onChange={(e) => setForm((f) => ({ ...f, fileName: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="URL / link"
              placeholder="https://..."
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Category"
              placeholder="e.g. Contract, Proposal, PO"
              value={form.docCategory}
              onChange={(e) => setForm((f) => ({ ...f, docCategory: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Notes"
              multiline
              minRows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
            />
            {saveError && (
              <Typography variant="caption" color="error">
                {saveError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={submitAdd} disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteId !== null} onClose={() => !deleting && setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove document link?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            This only removes the link from this record — it does not delete the underlying document.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CrmAttachmentsPanel;
